import styled from '@emotion/styled';
import { Skeleton } from '@mui/material';
import { ReactNode } from 'react';
import { CategoryBackground, ChartData } from '../chartUtils';
import BasicLegend, { BasicLegendProps } from './BasicLegend';
import CompleteLegend, { CompleteLegendProps } from './CompleteLegend';
import TileLegend from './TileLegend';

type ChartLegendProps = {
  loading: boolean | null;
  skeletonOrientation: 'vertical' | 'horizontal';
} & (BasicLegendProps | CompleteLegendProps);

// base props needed for either legend configuration
export type LegendBaseProps = {
  data: ChartData;
  colors: CategoryBackground[];
  mouseOverCallback: (index: number) => void;
  mouseLeaveCallback: () => void;
};

const ChartLegend = ({ data, loading, skeletonOrientation, ...legendProps }: ChartLegendProps) => {
  const isMatrixed = !!(typeof data.values[0] !== "number" && data.values[0]?.data);

  const renderLegend = () => {
    switch (data.legend) {
      case 'tile':
        return <TileLegend {...legendProps} data={data} />;
      case 'complete':
        return <CompleteLegend {...legendProps} data={data} />;
      case 'square':
      case 'circle':
      case 'value':
        return <BasicLegend {...legendProps} marker={data.legend} data={data} />;
      default:
        return <></>;
    }
  };

  return (
    <Wrapper id="chart-legend">
      {loading ? (
        <SkeletonWrapper orientation={skeletonOrientation}>
          {Array.from(Array(isMatrixed ? data.values.length : data.labels.length).keys()).map((item) => (
            <Skeleton key={item} variant="text" />
          ))}
        </SkeletonWrapper>
      ) : (
        <div>{renderLegend()}</div>
      )}
    </Wrapper>
  );
};

export default ChartLegend;

const Wrapper = styled.div`
  // width: 100%;
`;

const SkeletonWrapper = styled.div<{ orientation: ChartLegendProps['skeletonOrientation'] }>`
  display: flex;
  flex-direction: ${({ orientation }) => (orientation === 'vertical' ? 'column' : 'row')};
  row-gap: ${({ theme }) => theme.spacing(1)};
  column-gap: ${({ theme }) => theme.spacing(2)};
  width: 100%;
  align-items: center;
  justify-content: flex-start;

  .MuiSkeleton-root {
    width: ${({ orientation }) => (orientation === 'vertical' ? '200px' : '50px')};
    height: 20px;

    @media (min-width: ${({ theme }) => theme.breakpoints.values['md']}px) {
      max-width: ${({ orientation }) => (orientation === 'vertical' ? '150px' : '50px')};
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.values['lg']}px) {
      width: ${({ orientation }) => (orientation === 'vertical' ? '200px' : '75px')};
    }
  }
`;
